import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { productTotal, type DeliveryInfo, type CartItem } from '@/services/shopService'

interface PurchaseModalProps {
  items: CartItem[]
  userCoins: number
  onClose: () => void
  onConfirm: (deliveryInfo?: DeliveryInfo) => Promise<void>
  isLoading: boolean
  deliveryDraft?: DeliveryInfo
  onDeliveryChange?: (delivery: DeliveryInfo) => void
}

function readDelivery(form: HTMLFormElement): DeliveryInfo {
  const data = new FormData(form)
  const field = (name: string) => {
    const value = data.get(name)
    return typeof value === 'string' ? value.trim() : ''
  }
  return {
    name: field('name'),
    phone: field('phone'),
    address: field('address'),
    city: field('city'),
    postalCode: field('postalCode'),
    notes: field('notes'),
  }
}

export default function PurchaseModal({
  items,
  userCoins,
  onClose,
  onConfirm,
  isLoading,
  deliveryDraft,
  onDeliveryChange,
}: PurchaseModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const pending = useRef(false)
  const id = useId()
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const busy = isLoading || submitting
  const requiresDelivery = items.some(({ product }) => product.requiresDelivery)
  const subtotal = items.reduce((sum, { product }) => sum + product.price, 0)
  const total = items.reduce((sum, { product }) => sum + productTotal(product), 0)
  const missing = Math.max(0, total - userCoins)
  const format = (value: number) => `${value.toLocaleString('cs-CZ')} mincí`

  useEffect(() => {
    const dialog = dialogRef.current
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    dialog?.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      dialog?.close()
      document.body.style.overflow = previousOverflow
      if (previousFocus instanceof HTMLElement) previousFocus.focus()
    }
  }, [])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (pending.current || isLoading) return
    if (!items.length) return
    if (missing > 0) {
      setError(`Na nákup včetně dopravy ti chybí ${format(missing)}.`)
      return
    }

    let deliveryInfo: DeliveryInfo | undefined
    if (requiresDelivery) {
      deliveryInfo = readDelivery(event.currentTarget)
      if (
        !deliveryInfo.name ||
        !deliveryInfo.phone ||
        !deliveryInfo.address ||
        !deliveryInfo.city ||
        !deliveryInfo.postalCode
      ) {
        setError('Vyplň všechna povinná dodací pole.')
        return
      }
      if (!/^(\+420)?[0-9]{9}$/.test(deliveryInfo.phone.replace(/\s/g, ''))) {
        setError('Zadej české telefonní číslo, například +420 123 456 789.')
        return
      }
    }

    pending.current = true
    setSubmitting(true)
    setError('')
    try {
      await onConfirm(deliveryInfo)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Nákup se nezdařil. Zkus to znovu.')
    } finally {
      pending.current = false
      setSubmitting(false)
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="ac-checkout"
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-product`}
      onCancel={(event) => {
        event.preventDefault()
        if (!busy) onClose()
      }}
    >
      <form
        onInput={(event) => {
          if (requiresDelivery) onDeliveryChange?.(readDelivery(event.currentTarget))
        }}
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
        aria-busy={busy}
      >
        <div className="ac-checkout-heading">
          <h2 id={`${id}-title`}>Dokončit nákup</h2>
          <button
            type="button"
            className="ac-shop-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Zavřít nákup"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <p id={`${id}-product`} className="ac-checkout-product">
          Zkontroluj celý výběr.
          {requiresDelivery
            ? ' Dodací údaje stačí vyplnit jednou.'
            : 'Tyto produkty nepotřebují doručení.'}
        </p>
        <ul className="ac-checkout-items">
          {items.map(({ product, size }) => (
            <li key={product.id}>
              <span>
                {product.name}
                {size && <small>Varianta: {size}</small>}
              </span>
              <strong>{format(product.price)}</strong>
            </li>
          ))}
        </ul>
        {requiresDelivery && (
          <fieldset className="ac-delivery-fields" disabled={busy}>
            <legend>Dodací údaje</legend>
            <p>Všechna pole kromě poznámky jsou povinná.</p>
            <label htmlFor={`${id}-name`}>
              Jméno a příjmení
              <input
                id={`${id}-name`}
                name="name"
                defaultValue={deliveryDraft?.name}
                maxLength={255}
                autoComplete="shipping name"
                required
              />
            </label>
            <label htmlFor={`${id}-phone`}>
              Telefon
              <input
                id={`${id}-phone`}
                name="phone"
                defaultValue={deliveryDraft?.phone}
                maxLength={50}
                type="tel"
                autoComplete="shipping tel"
                placeholder="+420 123 456 789"
                required
              />
            </label>
            <label htmlFor={`${id}-address`}>
              Ulice a číslo
              <input
                id={`${id}-address`}
                name="address"
                defaultValue={deliveryDraft?.address}
                autoComplete="shipping street-address"
                required
              />
            </label>
            <div className="ac-delivery-row">
              <label htmlFor={`${id}-city`}>
                Město
                <input
                  id={`${id}-city`}
                  name="city"
                  defaultValue={deliveryDraft?.city}
                  maxLength={100}
                  autoComplete="shipping address-level2"
                  required
                />
              </label>
              <label htmlFor={`${id}-postalCode`}>
                PSČ
                <input
                  id={`${id}-postalCode`}
                  name="postalCode"
                  defaultValue={deliveryDraft?.postalCode}
                  maxLength={20}
                  autoComplete="shipping postal-code"
                  required
                />
              </label>
            </div>
            <label htmlFor={`${id}-notes`}>
              Poznámka k doručení <span>(nepovinná)</span>
              <textarea
                id={`${id}-notes`}
                name="notes"
                defaultValue={deliveryDraft?.notes}
                rows={2}
              />
            </label>
          </fieldset>
        )}

        <dl className="ac-checkout-totals">
          <div>
            <dt>Produkty</dt>
            <dd>{format(subtotal)}</dd>
          </div>
          {requiresDelivery && (
            <div>
              <dt>Doprava za všechny produkty</dt>
              <dd>{format(total - subtotal)}</dd>
            </div>
          )}
          <div className="ac-checkout-total">
            <dt>Celkem</dt>
            <dd>{format(total)}</dd>
          </div>
          <div>
            <dt>Tvůj zůstatek</dt>
            <dd>{format(userCoins)}</dd>
          </div>
          {missing === 0 && (
            <div>
              <dt>Po nákupu ti zůstane</dt>
              <dd>{format(userCoins - total)}</dd>
            </div>
          )}
        </dl>
        {missing > 0 && (
          <p className="ac-shop-alert" role="status">
            Na tento nákup ti chybí {format(missing)}.
          </p>
        )}
        {error && (
          <p className="ac-shop-alert" role="alert">
            {error}
          </p>
        )}
        {busy && (
          <p role="status" className="ac-checkout-status">
            Zpracováváme nákup. Počkej na potvrzení.
          </p>
        )}
        <div className="ac-checkout-actions">
          <button
            type="button"
            className="ac-shop-button ac-shop-button-secondary"
            disabled={busy}
            onClick={onClose}
          >
            Zpět do košíku
          </button>
          <button
            type="submit"
            className="ac-shop-button"
            disabled={
              busy || missing > 0 || !items.length || items.some(({ product }) => !product.isActive)
            }
          >
            {busy ? 'Zpracováváme…' : `Koupit za ${format(total)}`}
          </button>
        </div>
      </form>
    </dialog>
  )
}
